import React, { useState, useEffect, Suspense } from 'react';
import { doc, onSnapshot, updateDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { deleteUser } from 'firebase/auth';
import { useFirebase } from '../App';
import Stepper, { Step } from '../components/Stepper';

// PERFORMANCE: Lazy load the Lanyard so the page loads instantly
// The 3D model will load in the background
const Lanyard = React.lazy(() => import('../components/Lanyard'));

const DelegatePortalPage = ({ navigate }) => {
    const { db, storage, user, isAuthReady, positionPaperSubmissionsOpen, auth } = useFirebase();
    
    // Core Data State
    const [delegateInfo, setDelegateInfo] = useState(null);
    const [announcement, setAnnouncement] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // UI State
    const [paperFile, setPaperFile] = useState(null);
    const [uploadStatus, setUploadStatus] = useState({ loading: false, message: '', progress: 0, isError: false });
    const [isStepperOpen, setIsStepperOpen] = useState(false);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

    // HELPER: Check if delegate is fully allocated
    const isAllocated = delegateInfo?.committeeAllocation 
        && delegateInfo?.countryAllocation 
        && delegateInfo.committeeAllocation !== 'Pending' 
        && delegateInfo.countryAllocation !== 'Pending';

    // --- 1. Fetch Data Listeners ---
    useEffect(() => {
        if (!isAuthReady) return;
        if (!user) {
            navigate('login');
            return;
        }

        setLoading(true);

        const delegateDocRef = doc(db, `users/${user.uid}/thessmunDelegateInfo`, 'myDelegateInfo');
        
        const unsubDelegate = onSnapshot(delegateDocRef, (docSnap) => {
            if (docSnap.exists()) {
                setDelegateInfo(docSnap.data());
            } else if (user.role === 'teacher') {
                navigate('teacher-dashboard');
            }
            setLoading(false);
        }, (err) => {
            console.error(err);
            setError("Could not load registration details.");
            setLoading(false);
        });

        const unsubAnnounce = onSnapshot(doc(db, 'appSettings', 'announcements'), (doc) => {
            if (doc.exists()) setAnnouncement(doc.data().text);
        });

        return () => {
            unsubDelegate();
            unsubAnnounce();
        };
    }, [user, isAuthReady, db, navigate]);

    // --- 2. Action Handlers ---
    const handleFileSelection = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.type !== 'application/pdf') {
            setUploadStatus({ loading: false, message: 'Only PDF files are allowed.', isError: true });
            setPaperFile(null);
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setUploadStatus({ loading: false, message: 'File is too large (Max 10MB).', isError: true });
            setPaperFile(null);
            return;
        }
        setPaperFile(file);
        setUploadStatus({ message: '', isError: false, progress: 0 });
    };

    const handleUpload = async () => {
        if (!paperFile || !user) return;
        setUploadStatus({ loading: true, message: 'Starting upload...', progress: 0, isError: false });

        try {
            const storageRef = ref(storage, `position_papers/${user.uid}/${paperFile.name}`);
            const uploadTask = uploadBytesResumable(storageRef, paperFile);

            uploadTask.on('state_changed',
                (snapshot) => {
                    const prog = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setUploadStatus(prev => ({ ...prev, progress: prog, message: `Uploading: ${prog.toFixed(0)}%` }));
                },
                (err) => { throw err; },
                async () => {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    await updateDoc(doc(db, `users/${user.uid}/thessmunDelegateInfo`, 'myDelegateInfo'), {
                        positionPaperUrl: downloadURL,
                        positionPaperUploadedAt: new Date().toISOString()
                    });
                    setUploadStatus({ loading: false, message: 'Submitted successfully!', progress: 100, isError: false });
                    setPaperFile(null);
                }
            );
        } catch (err) {
            setUploadStatus({ loading: false, message: 'Upload failed: ' + err.message, isError: true });
        }
    };

    // --- Render Helpers ---
    const getStatusColor = (status) => {
        const s = (status || 'Pending').toLowerCase();
        if (s.includes('approved')) return 'text-green-600 bg-green-100 border-green-200';
        if (s.includes('rejected')) return 'text-red-600 bg-red-100 border-red-200';
        if (s.includes('waitlist')) return 'text-yellow-600 bg-yellow-100 border-yellow-200';
        return 'text-gray-600 bg-gray-100 border-gray-200';
    };

    if (loading) return <div className="text-center p-10 animate-pulse">Loading Portal...</div>;
    if (error) return <div className="text-center p-10 text-red-600">{error}</div>;

    const showPendingMessage = !delegateInfo?.registrationStatus || delegateInfo?.registrationStatus === 'Pending';
    const showApprovedIncomplete = delegateInfo?.registrationStatus === 'Approved' && !delegateInfo?.isApplicationComplete;
    
    // Only show right column if stepper is done
    const showRightColumn = delegateInfo?.isApplicationComplete;

    return (
        <section className="container mx-auto px-4 py-8 animate-fade-in relative min-h-screen">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
                <div>
                    <h2 className="text-3xl font-bold">Delegate Portal</h2>
                    <p className="text-gray-500">Welcome, {user?.fullName}</p>
                </div>
                <button 
                    onClick={() => setIsProfileModalOpen(true)}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-2 px-4 rounded shadow-sm border border-gray-300"
                >
                    ⚙️ Manage Profile
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                
                {/* --- LEFT COLUMN (2/3) --- */}
                <div className="lg:col-span-2 space-y-6">
                    
                    {/* 1. Status Card */}
                    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200" style={{ backgroundColor: 'var(--color-section1-card-bg)' }}>
                        <h3 className="text-xl font-bold mb-4">Registration Status</h3>
                        
                        <div className="flex items-center justify-between mb-4">
                            <span className="font-medium">Current Status:</span>
                            <span className={`px-3 py-1 rounded-full text-sm font-bold border ${getStatusColor(delegateInfo?.registrationStatus)}`}>
                                {delegateInfo?.registrationStatus || 'Pending'}
                            </span>
                        </div>

                        {showPendingMessage && (
                            <div className="bg-blue-50 text-blue-800 p-4 rounded text-sm border-l-4 border-blue-500">
                                <p className="font-bold mb-1">Application Under Review</p>
                                <p>We are currently reviewing your application. You will be notified via email once your status changes.</p>
                            </div>
                        )}

                        {showApprovedIncomplete && (
                            <div className="bg-green-50 border border-green-200 p-6 rounded-lg text-center mt-4">
                                <h4 className="text-lg font-bold text-green-800 mb-2">You're Approved! 🎉</h4>
                                <p className="text-green-700 mb-4">Please complete the final steps to secure your committee and country allocation.</p>
                                <button 
                                    onClick={() => setIsStepperOpen(true)}
                                    className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-full shadow transition-transform transform hover:scale-105"
                                >
                                    Complete Registration Steps
                                </button>
                            </div>
                        )}
                    </div>

                    {/* 2. Announcements */}
                    <div className="bg-yellow-50 p-6 rounded-lg shadow-md border border-yellow-200">
                        <h3 className="text-lg font-bold text-yellow-800 mb-2 flex items-center">
                            <span className="mr-2">📢</span> Announcements
                        </h3>
                        <p className="text-sm text-yellow-900 whitespace-pre-wrap">
                            {announcement || "No new announcements."}
                        </p>
                    </div>

                    {/* 3. Position Paper */}
                    {delegateInfo?.isApplicationComplete && (
                        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200" style={{ backgroundColor: 'var(--color-section1-card-bg)' }}>
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-xl font-bold">Position Paper</h3>
                                <span className={`text-xs font-bold px-2 py-1 rounded ${positionPaperSubmissionsOpen ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                    {positionPaperSubmissionsOpen ? 'OPEN' : 'CLOSED'}
                                </span>
                            </div>
                            
                            {!positionPaperSubmissionsOpen ? (
                                <p className="text-gray-500 italic">Submissions are currently closed.</p>
                            ) : (
                                <div>
                                    {delegateInfo.positionPaperUrl && (
                                        <div className="mb-4 p-3 bg-green-50 border border-green-100 rounded flex justify-between items-center">
                                            <span className="text-green-800 text-sm font-semibold">✓ Paper Submitted</span>
                                            <a href={delegateInfo.positionPaperUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline text-sm">Download PDF</a>
                                        </div>
                                    )}
                                    
                                    <div className="flex flex-col sm:flex-row gap-4 items-center mt-4">
                                        <input type="file" accept=".pdf" onChange={handleFileSelection} 
                                               className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"/>
                                        <button 
                                            onClick={handleUpload} 
                                            disabled={!paperFile || uploadStatus.loading}
                                            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold py-2 px-4 rounded shadow whitespace-nowrap"
                                        >
                                            {uploadStatus.loading ? 'Uploading...' : 'Upload PDF'}
                                        </button>
                                    </div>
                                    {uploadStatus.message && (
                                        <p className={`mt-2 text-sm ${uploadStatus.isError ? 'text-red-600' : 'text-green-600'}`}>
                                            {uploadStatus.message}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* --- RIGHT COLUMN (1/3) --- */}
                <div className="lg:col-span-1 space-y-6">
                    {/* LOGIC: Only show ANY content here if stepper is complete */}
                    {showRightColumn && (
                        <>
                            

                            {/* 2. Interactive Lanyard OR Placeholder */}
                            {isAllocated ? (
                                <div className="w-full h-[600px] rounded-lg overflow-hidden relative border border-gray-200 shadow-inner bg-gray-100">
                                    {/* SUSPENSE: This ensures the 3D model doesn't block the rest of the page from loading */}
                                    <Suspense fallback={
                                        <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                                            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                            <p>Loading Digital ID...</p>
                                        </div>
                                    }>
                                        <Lanyard 
                                            name={user.fullName} 
                                            committee={delegateInfo.committeeAllocation}
                                            country={delegateInfo.countryAllocation}
                                        />
                                    </Suspense>
                                </div>
                            ) : (
                                <div className="w-full h-[300px] rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 p-6 text-center bg-gray-50">
                                    <span className="text-4xl mb-3">🆔</span>
                                    <p className="font-semibold text-gray-600">ID Card Generating...</p>
                                    <p className="text-sm mt-2">Your digital lanyard will appear here automatically once your specific Country and Committee are confirmed by the staff.</p>
                                </div>
                            )}
                            {/* 1. Allocation Details Card */}
                            <div className="bg-white p-5 rounded-lg shadow-md border border-gray-200" style={{ backgroundColor: 'var(--color-section1-card-bg)' }}>
                                <h4 className="font-bold text-gray-500 uppercase text-xs mb-3 tracking-wider">Allocation Details</h4>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center border-b pb-2 border-gray-100">
                                        <span className="text-sm text-gray-600">Committee</span>
                                        <span className="font-bold text-blue-700">{delegateInfo.committeeAllocation || 'Pending'}</span>
                                    </div>
                                    <div className="flex justify-between items-center pt-1">
                                        <span className="text-sm text-gray-600">Country</span>
                                        <span className="font-bold text-blue-700">{delegateInfo.countryAllocation || 'Pending'}</span>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* --- MODALS --- */}
            {isStepperOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-y-auto relative" style={{ backgroundColor: 'var(--color-section1-card-bg)' }}>
                        <button onClick={() => setIsStepperOpen(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-3xl z-10">&times;</button>
                        <div className="p-8">
                            <FinalStepsStepper onFinalize={() => setIsStepperOpen(false)} />
                        </div>
                    </div>
                </div>
            )}

            {isProfileModalOpen && (
                <ProfileSettingsModal 
                    user={user} 
                    delegateInfo={delegateInfo} 
                    onClose={() => setIsProfileModalOpen(false)} 
                    db={db}
                    auth={auth}
                    navigate={navigate}
                />
            )}
        </section>
    );
};

// --- Sub-Components (ProfileSettingsModal & FinalStepsStepper) ---

const ProfileSettingsModal = ({ user, delegateInfo, onClose, db, auth, navigate }) => {
    const [formData, setFormData] = useState({
        fullName: user.fullName || '',
        phone: delegateInfo?.phone || '',
    });
    const [loading, setLoading] = useState(false);

    const handleUpdate = async () => {
        setLoading(true);
        try {
            await updateDoc(doc(db, 'users', user.uid), { fullName: formData.fullName });
            await updateDoc(doc(db, `users/${user.uid}/thessmunDelegateInfo`, 'myDelegateInfo'), { 
                fullName: formData.fullName,
            });
            alert('Profile updated successfully.');
            onClose();
        } catch (e) {
            console.error(e);
            alert('Failed to update profile.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAccount = async () => {
        if (!window.confirm("WARNING: Are you sure you want to delete your account?")) return;
        const confirmEmail = prompt("Please type your email to confirm deletion:");
        if (confirmEmail !== user.email) {
            alert("Email does not match.");
            return;
        }

        setLoading(true);
        try {
            await deleteDoc(doc(db, `users/${user.uid}/thessmunDelegateInfo`, 'myDelegateInfo'));
            await deleteDoc(doc(db, 'users', user.uid));
            await deleteUser(auth.currentUser);
            navigate('home');
        } catch (error) {
            console.error(error);
            alert("Failed to delete account: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 relative" style={{ backgroundColor: 'var(--color-section1-card-bg)' }}>
                <button onClick={onClose} className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
                <h3 className="text-xl font-bold mb-4">Manage Profile</h3>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-bold mb-1">Email (Read-Only)</label>
                        <input value={user.email} disabled className="w-full p-2 border rounded bg-gray-100 opacity-70 cursor-not-allowed" />
                    </div>
                    <div>
                        <label className="block text-sm font-bold mb-1">Full Name</label>
                        <input value={formData.fullName} onChange={e => setFormData({...formData, fullName: e.target.value})} className="w-full p-2 border rounded text-black" />
                    </div>
                </div>
                <div className="mt-6 flex flex-col gap-3">
                    <button onClick={handleUpdate} disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 rounded">{loading ? 'Saving...' : 'Update Information'}</button>
                    <hr className="border-gray-200 my-2"/>
                    <button onClick={handleDeleteAccount} disabled={loading} className="w-full bg-white border border-red-500 text-red-600 hover:bg-red-50 font-bold py-2 rounded">Delete Account</button>
                </div>
            </div>
        </div>
    );
};

const FinalStepsStepper = ({ onFinalize }) => {
    const { db, user } = useFirebase();
    const [data, setData] = useState({
        committeePref1: '', committeePref2: '',
        countryPref1: '', countryPref2: '',
        foodPreferences: '', healthProblems: '', otherNotes: ''
    });

    const committees = ['General Assembly', 'Security Council', 'Human Rights Council', 'ECOSOC', 'Historical Crisis', 'Press Corps'];
    const countries = ['USA', 'UK', 'China', 'Russia', 'France', 'Germany', 'Japan', 'Brazil', 'India', 'South Africa'];
    const handleChange = (e) => setData({ ...data, [e.target.name]: e.target.value });
    
    const savePreferences = async () => {
        try {
            await setDoc(doc(db, `users/${user.uid}/thessmunDelegateInfo`, 'finalRegistration'), {
                preferences: { committee: [data.committeePref1, data.committeePref2], country: [data.countryPref1, data.countryPref2] },
                health: { food: data.foodPreferences, medical: data.healthProblems, notes: data.otherNotes },
                completedAt: new Date().toISOString()
            });
            await updateDoc(doc(db, `users/${user.uid}/thessmunDelegateInfo`, 'myDelegateInfo'), { isApplicationComplete: true });
            onFinalize();
        } catch (err) {
            console.error(err);
            alert("Failed to save.");
        }
    };

    // FIX: Increased padding (p-4) to prevent cropping of inputs/focus rings
    return (
        <Stepper initialStep={1} onFinalStepCompleted={savePreferences} backButtonText="Back" nextButtonText="Next">
            <Step><div className="p-4"><h2 className="text-2xl font-bold mb-2">Welcome!</h2><p>We need a few more details to finalize your spot.</p></div></Step>
            <Step><div className="p-4"><h2 className="text-xl font-bold mb-4">Committee Preferences</h2><div className="space-y-4"><select name="committeePref1" onChange={handleChange} className="w-full p-2 border rounded text-black mb-2"><option value="">Select 1st Preference</option>{committees.map(c => <option key={c} value={c}>{c}</option>)}</select><select name="committeePref2" onChange={handleChange} className="w-full p-2 border rounded text-black"><option value="">Select 2nd Preference</option>{committees.map(c => <option key={c} value={c}>{c}</option>)}</select></div></div></Step>
            <Step><div className="p-4"><h2 className="text-xl font-bold mb-4">Country Preferences</h2><div className="space-y-4"><select name="countryPref1" onChange={handleChange} className="w-full p-2 border rounded text-black mb-2"><option value="">Select 1st Preference</option>{countries.map(c => <option key={c} value={c}>{c}</option>)}</select><select name="countryPref2" onChange={handleChange} className="w-full p-2 border rounded text-black"><option value="">Select 2nd Preference</option>{countries.map(c => <option key={c} value={c}>{c}</option>)}</select></div></div></Step>
            <Step><div className="p-4"><h2 className="text-xl font-bold mb-4">Health & Dietary</h2><textarea name="foodPreferences" placeholder="Dietary Restrictions" onChange={handleChange} className="w-full p-2 border rounded h-24 mb-4 text-black"/><textarea name="healthProblems" placeholder="Medical Conditions" onChange={handleChange} className="w-full p-2 border rounded h-24 text-black"/></div></Step>
            <Step><div className="p-4"><h2 className="text-xl font-bold mb-4">All Set?</h2><p>Click "Complete" to submit.</p></div></Step>
        </Stepper>
    );
};

export default DelegatePortalPage;